import React, {
  KeyboardEvent, MouseEvent, useCallback, useId, useMemo, useRef, useState,
} from 'react';

import Icon from 'components/DesignSystem/Icon';
import Segmented, { SegmentedOption } from 'components/DesignSystem/Segmented';
import FilterField from 'components/Flux/Personalized/MyStudies/FilterBar/FilterField';
import RangePopover from 'components/Flux/Personalized/MyStudies/FilterBar/PeriodFilter/RangePopover';
import ReferencePopover, {
  DATE_REF_LABELS,
  ReferenceMenuCloseReason,
} from 'components/Flux/Personalized/MyStudies/FilterBar/PeriodFilter/ReferencePopover';
import { PopoverDismissReason } from 'components/Flux/Personalized/MyStudies/FilterBar/PeriodFilter/usePopoverDismiss';
import {
  MyStudiesDateInterval,
  MyStudiesDateRef,
  MyStudiesFilters,
  MyStudiesFiltersChange,
  MyStudiesPeriod,
} from 'types/Flux/Personalized/myStudiesFilters';

import './index.scss';

/** Rótulo do campo (.dc.html:176). */
const PERIOD_LABEL = 'PERÍODO · REFERENTE À';

/** Segmentos, na ordem do protótipo (.dc.html:568). */
const PERIOD_SEGMENTS: ReadonlyArray<SegmentedOption<MyStudiesPeriod>> = [
  { value: 'hoje', label: 'Hoje' },
  { value: 'semana', label: 'Esta semana' },
  { value: 'mes', label: 'Este mês' },
  { value: 'intervalo', label: 'Intervalo…' },
  { value: 'tudo', label: 'Tudo' },
];

type PeriodPopover = 'reference' | 'range';

export interface PeriodFilterProps {
  /** Filtros aplicados (`useMyStudiesFilters().filters`): só a referência e o período são lidos. */
  filters: Pick<MyStudiesFilters, 'ref' | 'periodo' | 'intervalo'>;
  /**
   * `useMyStudiesFilters().setFilters`: replace na URL e volta para a página 1. Recebe
   * `{ ref }`, `{ periodo }` ou, no "Aplicar" do intervalo, `{ periodo: 'intervalo', intervalo }`.
   */
  onFiltersChange: (change: MyStudiesFiltersChange) => void;
  /**
   * "Criação do estudo" escolhível. Padrão `false` (D8): a opção fica `aria-disabled`, com dica. A
   * casca passa a mesma fonte ao `useMyStudiesFilters`, que troca `ref=created` da URL e da sessão
   * por `last_exec`; a #2006 liga.
   */
  createdRefAvailable?: boolean;
}

/**
 * Campo "PERÍODO · REFERENTE À" da barra de Meus Estudos (#1996), no `periodSlot` da `FilterBar`
 * (.dc.html:174-203).
 * - Pílula da referência colada ao rótulo (nota 1, .dc.html:35-37): abre o `ReferencePopover`.
 * - `Segmented` filled: Hoje | Esta semana | Este mês | Intervalo… | Tudo. Os presets aplicam na
 *   hora; "Intervalo…" só abre o `RangePopover`.
 *
 * D23, opção B (rascunho): as datas do intervalo são locais ao popover e só "Aplicar" grava
 * `{ periodo: 'intervalo', intervalo }`; fechar sem aplicar mantém o período anterior. No
 * protótipo, clicar em "Intervalo…" já aplicava o período com o intervalo da tela, e cada data
 * aplicava ao mudar (.dc.html:670 e 718-719): o segmento ficava azul na hora e o "Aplicar" só
 * fechava. Aqui, enquanto o rascunho está aberto, o segmento marcado continua o do período
 * aplicado; "Intervalo…" diz que o popover está aberto só no `aria-expanded` (nenhum gatilho da
 * barra muda de cor ao abrir; o azul quer dizer filtro ativo) e fica azul depois do "Aplicar".
 *
 * Controlado por props, como o resto da barra: quem guarda os filtros é o `useMyStudiesFilters`
 * da tela. Só um popover fica aberto por vez. Esc, "Aplicar", a escolha da referência e o clique
 * na pílula com o menu aberto devolvem o foco ao gatilho; o clique fora deixa o foco onde se
 * clicou.
 */
export default function PeriodFilter({
  filters,
  onFiltersChange,
  createdRefAvailable = false,
}: PeriodFilterProps) {
  const { ref: dateRef, periodo, intervalo } = filters;
  const labelTextId = useId();
  const pillId = useId();
  const menuId = useId();
  const intervalSegmentId = useId();
  const rangeId = useId();
  const labelRowRef = useRef<HTMLSpanElement>(null);
  const pillRef = useRef<HTMLButtonElement>(null);
  const segmentsRef = useRef<HTMLDivElement>(null);
  const menuHostRef = useRef<HTMLDivElement>(null);
  const rangeHostRef = useRef<HTMLDivElement>(null);
  const [openPopover, setOpenPopover] = useState<PeriodPopover | null>(null);
  const referenceOpen = openPopover === 'reference';
  const rangeOpen = openPopover === 'range';

  // Fecha só se ainda for o aberto: o clique que abre um popover é o clique fora do outro, e o
  // `onHide` do outro chega depois do clique
  const closePopover = useCallback((popover: PeriodPopover) => {
    setOpenPopover((current) => (current === popover ? null : current));
  }, []);

  const focusPill = useCallback(() => pillRef.current?.focus(), []);
  const focusIntervalSegment = useCallback(() => {
    document.getElementById(intervalSegmentId)?.focus();
  }, [intervalSegmentId]);

  // Fechar pela pílula deixa o foco nela: o item focado do menu sai da tela junto com ele
  const toggleReference = useCallback(() => {
    if (!referenceOpen) {
      setOpenPopover('reference');
      return;
    }
    closePopover('reference');
    focusPill();
  }, [referenceOpen, closePopover, focusPill]);

  // Com o menu aberto, o `mousedown` na pílula não leva o foco para ela: o foco saindo do menu o
  // fecharia antes do clique, e o clique o reabriria
  const handlePillMouseDown = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    if (referenceOpen) event.preventDefault();
  }, [referenceOpen]);

  // Setas na pílula fechada também abrem o menu (o foco vai para a referência aplicada)
  const handlePillKeyDown = useCallback((event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    setOpenPopover('reference');
  }, []);

  const handleReferenceSelect = useCallback((ref: MyStudiesDateRef) => {
    onFiltersChange({ ref });
    closePopover('reference');
    focusPill();
  }, [onFiltersChange, closePopover, focusPill]);

  const handleReferenceClose = useCallback((reason: ReferenceMenuCloseReason) => {
    closePopover('reference');
    if (reason === 'escape') focusPill();
  }, [closePopover, focusPill]);

  const handlePeriod = useCallback((value: MyStudiesPeriod) => {
    if (value === 'intervalo') {
      setOpenPopover('range');
      return;
    }
    setOpenPopover(null);
    onFiltersChange({ periodo: value });
  }, [onFiltersChange]);

  const handleApply = useCallback((interval: MyStudiesDateInterval) => {
    onFiltersChange({ periodo: 'intervalo', intervalo: interval });
    closePopover('range');
    focusIntervalSegment();
  }, [onFiltersChange, closePopover, focusIntervalSegment]);

  const handleRangeClose = useCallback((reason: PopoverDismissReason) => {
    closePopover('range');
    if (reason === 'escape') focusIntervalSegment();
  }, [closePopover, focusIntervalSegment]);

  const segments = useMemo(() => PERIOD_SEGMENTS.map((segment) => (
    segment.value === 'intervalo'
      ? {
        ...segment,
        id: intervalSegmentId,
        ariaHasPopup: 'dialog' as const,
        ariaExpanded: rangeOpen,
        ariaControls: rangeOpen ? rangeId : undefined,
      }
      : segment
  )), [intervalSegmentId, rangeId, rangeOpen]);

  // O nome da pílula e dos segmentos é o rótulo mais a referência: "PERÍODO · REFERENTE À Última
  // execução" (como o "STATUS <seleção>" do StatusFilter)
  const labelledBy = `${labelTextId} ${pillId}`;
  const label = (
    <span ref={labelRowRef} className="my-studies-period-label">
      <span id={labelTextId}>{PERIOD_LABEL}</span>
      <button
        ref={pillRef}
        id={pillId}
        type="button"
        className="my-studies-period-reference"
        aria-labelledby={labelledBy}
        aria-haspopup="menu"
        aria-expanded={referenceOpen}
        aria-controls={referenceOpen ? menuId : undefined}
        onMouseDown={handlePillMouseDown}
        onClick={toggleReference}
        onKeyDown={handlePillKeyDown}
      >
        <span>{DATE_REF_LABELS[dateRef]}</span>
        <span className="my-studies-period-reference-chevron" aria-hidden="true">
          <Icon iconType="down_arrow" color="currentColor" size="small" />
        </span>
      </button>
    </span>
  );

  return (
    <FilterField label={label} className="my-studies-period-filter">
      {/* Os popovers são montados logo depois do gatilho na ordem do DOM (`display: contents`,
          sem ocupar espaço): o Tab segue do menu para os segmentos, e o leitor de tela encontra
          cada um junto do seu gatilho */}
      <div ref={menuHostRef} className="my-studies-period-popover-host" />
      <div ref={segmentsRef} className="my-studies-period-segments">
        <Segmented
          variant="filled"
          fill={false}
          options={segments}
          value={periodo}
          onChange={handlePeriod}
          ariaLabelledBy={labelledBy}
        />
      </div>
      <div ref={rangeHostRef} className="my-studies-period-popover-host" />
      <ReferencePopover
        show={referenceOpen}
        target={labelRowRef}
        container={menuHostRef}
        id={menuId}
        value={dateRef}
        createdRefAvailable={createdRefAvailable}
        onSelect={handleReferenceSelect}
        onClose={handleReferenceClose}
      />
      <RangePopover
        show={rangeOpen}
        target={segmentsRef}
        container={rangeHostRef}
        id={rangeId}
        dateRef={dateRef}
        interval={periodo === 'intervalo' ? intervalo : null}
        onApply={handleApply}
        onClose={handleRangeClose}
      />
    </FilterField>
  );
}
